import { useCallback, useEffect, useState } from 'react'

const storageKeyFor = (userId: string | undefined): string => `boxTruckLoads:preferences:${userId ?? 'anon'}`

/**
 * What the operator has chosen about the board itself, as against the search they are running.
 *
 * Separate from the column layout (`useBoxTruckColumns`) and from the per-tab filters: a column layout is a
 * table shape, filters belong to one tab and are persisted per window, and these are preferences about how
 * the board behaves for this person wherever they open it.
 */
export interface BoxTruckBoardPreferences {
    /**
     * Draw the route map as soon as a load is opened, instead of waiting for Expand.
     *
     * OFF by default, and that default is the whole reason this exists as a choice. Opening a load used to
     * mount a live map unasked — a route request, up to three geocodes and thirty-odd map tiles for every
     * load an operator glanced at, and the board is scanned by opening and closing loads in seconds (#12350
     * replaced it with a drawn placeholder). An operator who works one lane and wants the map every time can
     * have it back; the person flicking through fifty loads does not pay for it.
     */
    openMapOnExpand: boolean
}

const DEFAULTS: BoxTruckBoardPreferences = { openMapOnExpand: false }

/** Read what is stored, key by key — a stored shape outlives the code that wrote it. */
const readPreferences = (storageKey: string): BoxTruckBoardPreferences => {
    try {
        const raw = localStorage.getItem(storageKey)
        if (!raw) return { ...DEFAULTS }
        const stored = JSON.parse(raw) as Partial<Record<keyof BoxTruckBoardPreferences, unknown>>
        return { openMapOnExpand: typeof stored.openMapOnExpand === 'boolean' ? stored.openMapOnExpand : DEFAULTS.openMapOnExpand }
    } catch {
        // A blocked or malformed store is not a reason to render a different board: fall back to the defaults.
        return { ...DEFAULTS }
    }
}

export interface BoxTruckBoardPreferencesController {
    preferences: BoxTruckBoardPreferences
    setPreference: <K extends keyof BoxTruckBoardPreferences>(key: K, value: BoxTruckBoardPreferences[K]) => void
}

/**
 * Per USER, like the column layout — these follow the person, not the browser tab they happen to be in.
 * Written on change and read once per user, so a second board tab opened later starts from the same answer.
 */
export const useBoxTruckBoardPreferences = (userId: string | undefined): BoxTruckBoardPreferencesController => {
    const storageKey = storageKeyFor(userId)
    const [preferences, setPreferences] = useState<BoxTruckBoardPreferences>(() => readPreferences(storageKey))

    // Re-read when the user changes (a different operator in the same browser must not inherit the first's).
    useEffect(() => {
        setPreferences(readPreferences(storageKey))
    }, [storageKey])

    const setPreference = useCallback(
        <K extends keyof BoxTruckBoardPreferences>(key: K, value: BoxTruckBoardPreferences[K]) => {
            setPreferences((previous) => {
                const next = { ...previous, [key]: value }
                try {
                    localStorage.setItem(storageKey, JSON.stringify(next))
                } catch {
                    // Kept in memory for this session even when it cannot be stored — the toggle still works.
                }
                return next
            })
        },
        [storageKey]
    )

    return { preferences, setPreference }
}
